
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Check, ArrowRight, Shield, Users, Link as LinkIcon, Star } from 'lucide-react';
import { useBusinessPlans } from '@/hooks/useBusinessPlans';

const BusinessSignup = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedPlan, setSelectedPlan] = useState('');
  const { businessPlans, isLoading } = useBusinessPlans();

  const handlePlanSelect = (planId: string) => {
    setSelectedPlan(planId);
  };

  const handleContinue = () => {
    if (!selectedPlan) {
      toast({
        title: "Please select a plan",
        description: "You need to select a subscription plan to continue.",
        variant: "destructive",
      });
      return;
    }

    sessionStorage.setItem('selectedPlan', selectedPlan);
    navigate('/business-account-create');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-b from-white to-blue-50">
        <Header 
          isLoggedIn={false} 
          brandName="ReferMe.site" 
        />
        <main className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          <p className="ml-3">Loading plans...</p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-white to-blue-50">
      <Header 
        isLoggedIn={false} 
        brandName="ReferMe.site" 
      />
      
      <main className="flex-1 px-4 py-12 md:py-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-5xl font-bold text-primary-blue mb-4 animate-fade-in">
              Choose Your Business Plan
            </h1>
            <p className="text-xl mb-6 max-w-3xl mx-auto text-lime-400">
              Start your 7-day free trial today and see how our platform can help grow your business.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {businessPlans.map((plan) => (
              <Card 
                key={plan.id}
                className={`cursor-pointer transition-all duration-300 overflow-hidden relative hover:shadow-xl ${
                  selectedPlan === plan.id 
                    ? 'border-2 border-primary ring-4 ring-primary/20 shadow-lg transform scale-105' 
                    : 'hover:transform hover:scale-105 border border-gray-200'
                }`}
                onClick={() => handlePlanSelect(plan.id)}
              >
                {plan.recommended && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-1.5 px-3 text-center text-sm font-medium z-10">
                    {plan.badgeText || 'RECOMMENDED'}
                  </div>
                )}
                
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-2xl font-bold text-primary-blue">{plan.name}</CardTitle>
                      <CardDescription className="text-gray-600 mt-1">{plan.description}</CardDescription>
                    </div>
                    {selectedPlan === plan.id && (
                      <div className="bg-green-500 text-white p-1.5 rounded-full">
                        <Check className="h-5 w-5" />
                      </div>
                    )}
                  </div>
                </CardHeader>
                
                <CardContent>
                  <div className="mb-6">
                    <p className="text-4xl font-bold text-primary-blue">${plan.monthlyPrice}<span className="text-sm font-normal text-gray-500">/mo</span></p>
                    <p className="text-sm text-gray-500 mt-1">${Math.round(plan.quarterlyPrice / 3)}/mo if billed quarterly</p>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <Users className="h-5 w-5 text-green-500 mr-3 shrink-0 mt-0.5" />
                      <span className="text-gray-700">{plan.maxReferrals} referral users</span>
                    </li>
                    <li className="flex items-start">
                      <Shield className="h-5 w-5 text-green-500 mr-3 shrink-0 mt-0.5" />
                      <span className="text-gray-700">{plan.maxManagers} managers</span>
                    </li>
                    <li className="flex items-start">
                      <LinkIcon className="h-5 w-5 text-green-500 mr-3 shrink-0 mt-0.5" />
                      <span className="text-gray-700">{plan.maxReferralLinks} referral links</span>
                    </li>
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        {feature.included ? (
                          <Check className="h-5 w-5 text-green-500 mr-3 shrink-0 mt-0.5" />
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="h-5 w-5 text-gray-300 mr-3 shrink-0 mt-0.5"
                          >
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        )}
                        <span className={`${feature.included ? 'text-gray-700' : 'text-gray-400'}`}>
                          {feature.name}
                          {feature.included && feature.limit && (
                            <span className="text-xs text-gray-500 ml-1">(limit: {feature.limit})</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                
                <CardFooter className="pt-2 bg-gray-50">
                  <Button 
                    variant={selectedPlan === plan.id ? "default" : "outline"} 
                    className={`w-full ${selectedPlan === plan.id ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700' : ''}`}
                    onClick={() => handlePlanSelect(plan.id)}
                  >
                    {selectedPlan === plan.id ? "Selected" : "Select Plan"}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
          
          <div className="mt-16 text-center">
            <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8 border border-gray-100">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <Star className="h-8 w-8 text-blue-600" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-primary-blue mb-2">7-Day Trial</h3>
              <p className="text-gray-600 mb-6">
                Experience the full power of our platform with no commitment. Cancel anytime during your trial period.
              </p>
              <Button 
                size="lg" 
                onClick={handleContinue}
                disabled={!selectedPlan}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-10 py-6 rounded-lg shadow-md transition-all"
              >
                Continue to Account Setup <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <p className="mt-4 text-sm text-gray-500">
                By continuing, you'll be able to create your account and set up your referral program.
              </p>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default BusinessSignup;
